import { useState, useCallback } from 'react';
import { addToCalendar, CalendarEvent, getICSDataURL } from '../services/calendarService';

interface UseCalendarReturn {
  addEvent: (event: CalendarEvent, options?: { filename?: string; preferWebShare?: boolean }) => Promise<void>;
  getEventURL: (event: CalendarEvent) => string;
  isWebShareSupported: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for adding calendar events from a PWA
 * 
 * @example
 * ```tsx
 * const { addEvent, isWebShareSupported } = useCalendar();
 * 
 * const handleAddToCalendar = async () => {
 *   await addEvent({
 *     title: 'Meeting',
 *     description: 'Important meeting',
 *     location: 'Office',
 *     startDate: new Date('2024-01-15T10:00:00'),
 *     endDate: new Date('2024-01-15T11:00:00'),
 *   });
 * };
 * ```
 */
export const useCalendar = (): UseCalendarReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isWebShareSupported = typeof navigator !== 'undefined' && 'share' in navigator;

  const addEvent = useCallback(
    async (
      event: CalendarEvent,
      options?: { filename?: string; preferWebShare?: boolean }
    ): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        await addToCalendar(event, options);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to add event to calendar';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getEventURL = useCallback((event: CalendarEvent): string => {
    return getICSDataURL(event);
  }, []);

  return {
    addEvent,
    getEventURL,
    isWebShareSupported,
    loading,
    error,
  };
};

